import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { supabase } from '@/lib/supabase';
import { useProfile } from '@/lib/providers';
import { Database } from '@/types/supabase';
import { RichTextViewer } from '@/components/ui/rich-text-viewer';
import { ArrowLeft } from 'lucide-react';
import { normalizeImageUrl } from '@/lib/utils';

type NewsItem = Database['public']['Tables']['news_items']['Row'];

function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trimEnd() + '...';
}

export function TeamNews() {
  const { profile } = useProfile();
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<NewsItem | null>(null);

  const team = profile?.teams.at(profile.active_team_idx);

  useEffect(() => {
    if (!team) return;

    const fetchTeamNews = async () => {
      setLoading(true);

      const scopes: string[] = [
        `and(scope_type.eq.team,scope_id.eq.${team.id})`,
        `and(scope_type.eq.organization,scope_id.eq.${team.organization.id})`,
        `and(scope_type.eq.sector,scope_id.eq.${team.organization.sector.id})`,
      ];

      const { data } = await supabase
        .from('news_items')
        .select('*')
        .or(scopes.join(','))
        .eq('published', true)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (data) setNewsItems(data);
      setLoading(false);
    };

    fetchTeamNews();
  }, [team?.id]);

  if (loading) {
    return <p className="text-sm text-muted-foreground py-4">Laden...</p>;
  }

  if (newsItems.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        Er zijn momenteel geen nieuwsberichten voor dit team.
      </p>
    );
  }

  // Detail view
  if (selectedItem) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => setSelectedItem(null)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Terug naar overzicht
        </button>

        <Card className="overflow-hidden">
          {selectedItem.image_url && (
            <img
              src={normalizeImageUrl(selectedItem.image_url)}
              alt={selectedItem.title}
              className="w-full h-48 sm:h-64 object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          <div className="p-6">
            <h3 className="text-xl font-semibold">{selectedItem.title}</h3>
            {selectedItem.body && (
              <div className="mt-4">
                <RichTextViewer content={selectedItem.body} className="text-sm" />
              </div>
            )}
          </div>
        </Card>
      </div>
    );
  }

  // Grid view
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {newsItems.map((item) => {
        const preview = item.body ? truncateText(stripHtml(item.body), 120) : '';

        return (
          <Card
            key={item.id}
            className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setSelectedItem(item)}
          >
            <div className="p-6 space-y-3">
              {item.image_url && (
                <img
                  src={normalizeImageUrl(item.image_url)}
                  alt={item.title}
                  className="w-full h-48 object-cover rounded-md"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
              <h3 className="text-lg font-semibold">{item.title}</h3>
              {preview && (
                <p className="text-sm text-muted-foreground line-clamp-3">
                  {preview}
                </p>
              )}
              <span className="text-sm font-medium text-green-600">
                Lees meer
              </span>
            </div>
          </Card>
        );
      })}
    </div>
  );
}
